import { useState } from "react";
import type { InboxFilter } from "@school-sales-ai/shared";
import { InboxLabelFilter } from "@/components/inbox/InboxLabelFilter";
import { InboxRow } from "@/components/inbox/InboxRow";
import { LabelsProvider } from "@/components/labels/LabelsProvider";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useInbox } from "@/hooks/useInbox";
import { cn } from "@/lib/utils";

// Transferidos goes first: it is the one category where a person is waiting on a
// person. It is NOT the default filter, though — it is empty most days, and opening
// the Buzón on an empty list makes the whole page read as broken. The counter on the
// tab is what announces it.
const FILTERS: Array<{ value: InboxFilter; label: string }> = [
  { value: "handoff", label: "Transferidos" },
  { value: "unread", label: "No leídos" },
  { value: "waiting", label: "Esperando" },
  { value: "all", label: "Todos" },
];

const PAGE_SIZE = 25;

export function Inbox(): React.JSX.Element {
  // Local state, deliberately NOT the crm store: those filters belong to the
  // pipeline and are shared by useLeads / usePipeline / the facet counts.
  // Coupling the two pages' filter bars is a bug waiting to happen.
  const [filter, setFilter] = useState<InboxFilter>("unread");
  const [search, setSearch] = useState("");
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const [page, setPage] = useState(1);

  const { data, isLoading, isError } = useInbox({ filter, search, labelIds, page });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const handoffTotal = data?.handoffTotal ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function changeFilter(next: InboxFilter): void {
    setFilter(next);
    setPage(1);
  }

  const unreadBadge =
    data && data.unreadTotal > 0 ? (
      <Badge variant="secondary" className="text-xs">
        {data.unreadTotal}
      </Badge>
    ) : null;

  return (
    // One LabelsProvider for the page, not one per row: it holds the single
    // label query and the assign/unassign mutations CardLabels calls.
    <LabelsProvider>
      {/* Layout's <main> supplies no padding — every page owns its own. Desktop
          matches Reports (p-6, gap-6); mobile is edge-to-edge under the
          PageHeader, like the pipeline's card list, so rows use a phone's full
          width.

          This container does NOT scroll: the list below does. Chrome that stays
          put is what an inbox wants — the filters and the pager should not walk
          off screen while you read down a long list. */}
      <div className="flex flex-1 min-h-0 flex-col md:h-full md:gap-6 md:p-6">
        {/* ── Mobile chrome ─────────────────────────── */}
        <div className="shrink-0 md:hidden">
          <PageHeader
            title="Buzón"
            subtitle={data ? `${data.unreadTotal} sin leer` : undefined}
          />
        </div>

        {/* ── Desktop chrome ────────────────────────── */}
        <div className="hidden shrink-0 md:flex items-center gap-3">
          <h1 className="font-display text-2xl font-bold text-ink-900">Buzón</h1>
          {unreadBadge}
        </div>

        <div className="flex shrink-0 flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between md:px-0 md:py-0">
          <div className="flex gap-1 rounded-lg border border-ink-100 bg-surface p-1">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => changeFilter(f.value)}
                className={cn(
                  "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                  filter === f.value
                    ? "bg-ink-900 text-white"
                    : "text-ink-500 hover:bg-ink-50",
                )}
              >
                {f.label}
                {/* Only Transferidos carries a count, and only when it is not
                    zero. This tab replaces a WhatsApp alert the team can no
                    longer receive, so it has to say how many without being
                    opened — and say nothing at all when there are none. */}
                {f.value === "handoff" && handoffTotal > 0 && (
                  <span
                    className={cn(
                      "rounded-full px-1.5 text-[10px] font-semibold tabular-nums",
                      filter === f.value
                        ? "bg-white/20 text-white"
                        : "bg-red-100 text-red-700",
                    )}
                  >
                    {handoffTotal}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <InboxLabelFilter
              selected={labelIds}
              onChange={(next) => {
                setLabelIds(next);
                setPage(1);
              }}
            />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Buscar por nombre, teléfono o referencia…"
              className="sm:w-72"
            />
          </div>
        </div>

        {/* The scroller, and the reason `min-h-0` is spelled out: a flex item
            whose overflow is not `visible` has its automatic minimum size
            resolved to 0, so without this the algorithm silently crushes the
            list to whatever space is left and clips the last rows — with no
            scrollbar, because there is nothing left to scroll. */}
        <div className="flex-1 min-h-0 overflow-y-auto border-y border-ink-100 bg-surface md:rounded-lg md:border">
          {isLoading && (
            <p className="px-4 py-10 text-center text-sm text-ink-400">Cargando…</p>
          )}
          {isError && (
            <p className="px-4 py-10 text-center text-sm text-red-600">
              No se pudo cargar el buzón.
            </p>
          )}
          {!isLoading && !isError && items.length === 0 && (
            <p className="px-4 py-10 text-center text-sm text-ink-400">
              {filter === "handoff"
                ? "Nadie esperando asesor."
                : filter === "unread"
                  ? "Nada sin leer. Todo al día."
                  : "No hay conversaciones que mostrar."}
            </p>
          )}
          {/* Rendered in server order. The endpoint already sorts by the real last
              message; re-sorting here would silently diverge from it. */}
          {items.map((item) => (
            <InboxRow key={item.id} item={item} />
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex shrink-0 items-center justify-between px-4 py-3 md:px-0 md:py-0">
            <span className="text-xs text-ink-400">
              Página {page} de {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || isLoading}
                onClick={() => setPage((p) => p - 1)}
              >
                ← Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages || isLoading}
                onClick={() => setPage((p) => p + 1)}
              >
                Siguiente →
              </Button>
            </div>
          </div>
        )}
      </div>
    </LabelsProvider>
  );
}
